import Events from '../events'
import EventBus from '../event-bus'
import { PushDataLayerEvent } from '../core'
import { LogoTracker, LTEvents } from '~/lib/events/tracker'

/**
 * Ingests GA impression compatible data, batches it, and sends it via GTM
 *
 * https://developers.google.com/tag-manager/ecommerce-ga4#measure_productitem_list_viewsimpressions
 */
EventBus.$on(Events.EcommerceImpressions, impressions => {
  // Loop through the impressions sending in batches of 20
  let has_unsent_impressions = false
  let batch = []

  impressions.forEach((impression, index) => {
    batch.push(impression)
    has_unsent_impressions = true

    if ((index + 1) % 20 === 0) {
      push('view_item_list', batch)

      batch = []
      has_unsent_impressions = false
    }
  })

  // Send any remaining impressions
  if (has_unsent_impressions) {
    push('view_item_list', batch)
  }
})

/**
 * Registers a click event in ecommerce-land for products.
 *
 * https://developers.google.com/tag-manager/ecommerce-ga4#measure_productitem_list_clicks
 */
EventBus.$on(Events.EcommerceClicked, items => push('select_item', items))

/**
 * Handles telling Google a user is viewing the product details page for something or other.
 *
 * https://developers.google.com/tag-manager/ecommerce-ga4#measure_viewsimpressions_of_productitem_details
 */
EventBus.$on(Events.EcommerceDetailView, items => push('view_item', items))

/**
 * Handles adding a set of products to the Google shopping integration
 *
 * https://developers.google.com/tag-manager/ecommerce-ga4#add_a_product_to_a_shopping_cart
 */
EventBus.$on(Events.EcommerceAddToCart, items => push('add_to_cart', items))

/**
 * Handles removing an item from the Google EEC integration
 *
 * https://developers.google.com/tag-manager/ecommerce-ga4#remove_a_product_from_a_shopping_cart
 */
EventBus.$on(Events.EcommerceRemoveFromCart, items => {
  push('remove_from_cart', items)
})

/**
 * Starts a checkout flow.
 *
 * https://developers.google.com/tag-manager/ecommerce-ga4#measure_a_checkout
 */
EventBus.$on(Events.EcommerceCheckout, items => push('begin_checkout', items))

/**
 * Finalizes a checkout flow.
 *
 * https://developers.google.com/tag-manager/ecommerce-ga4#measure_purchases
 */
EventBus.$on(Events.EcommercePurchase, purchase => {
  PushDataLayerEvent({ event: 'purchase', ecommerce: { purchase } })
})

/**
 * When a user adds items to a wishlist.
 *
 * https://support.google.com/analytics/answer/9268036
 */
EventBus.$on(Events.EcommerceAddToWishlist, items => {
  push('add_to_wishlist', items)
})

/**
 * When a user views their cart.
 *
 * https://support.google.com/analytics/answer/9268036
 */
EventBus.$on(Events.EcommerceViewCart, items => push('view_cart', items))

/**
 * When a user selects an asset in the editor.
 *
 * https://developers.google.com/gtagjs/reference/ga4-events#select_content
 */
EventBus.$on(Events.EcommerceSelectContent, ({ list = 'editor', type, id }) => {
  PushDataLayerEvent({
    event: 'select_content',
    ecommerce: {
      content_type: type,
      item_id: id,
      list,
    },
  })
})

/**
 * Generic "push ecommerce items" handler
 *
 * @param {String} event The GA event name
 * @param {Array} items An Array of GA4 formatted product items
 */
function push(event, items) {
  PushDataLayerEvent({ event, ecommerce: { items } })
}
